import { AttackDetection } from "./resources/attackDetection.js";
import { AuthenticationManagement } from "./resources/authenticationManagement.js";
import { Cache } from "./resources/cache.js";
import { ClientPolicies } from "./resources/clientPolicies.js";
import { Clients } from "./resources/clients.js";
import { ClientScopes } from "./resources/clientScopes.js";
import { Components } from "./resources/components.js";
import { Groups } from "./resources/groups.js";
import { IdentityProviders } from "./resources/identityProviders.js";
import { Realms } from "./resources/realms.js";
import { Organizations } from "./resources/organizations.js";
import { Workflows } from "./resources/workflows.js";
import { Roles } from "./resources/roles.js";
import { ServerInfo } from "./resources/serverInfo.js";
import { Users } from "./resources/users.js";
import { UserStorageProvider } from "./resources/userStorageProvider.js";
import { WhoAmI } from "./resources/whoAmI.js";
import { getToken } from "./utils/auth.js";
import { defaultBaseUrl, defaultRealm } from "./utils/constants.js";
import { decodeToken } from "./utils/decode.js";
const MIN_VALIDITY = 5; // in seconds
export class KeycloakAdminClient {
    // Resources
    users;
    userStorageProvider;
    groups;
    roles;
    organizations;
    workflows;
    clients;
    realms;
    clientScopes;
    clientPolicies;
    identityProviders;
    components;
    serverInfo;
    whoAmI;
    attackDetection;
    authenticationManagement;
    cache;
    // Members
    baseUrl;
    realmName;
    scope;
    accessToken;
    refreshToken;
    timeout;
    enableExperimentalApis;
    #requestOptions;
    #globalRequestArgOptions;
    #tokenProvider;
    #accessTokenDecoded;
    #refreshTokenDecoded;
    #credentials;
    constructor(connectionConfig) {
        this.baseUrl = connectionConfig?.baseUrl || defaultBaseUrl;
        this.realmName = connectionConfig?.realmName || defaultRealm;
        this.timeout = connectionConfig?.timeout;
        this.enableExperimentalApis =
            connectionConfig?.enableExperimentalApis ?? false;
        this.#requestOptions = connectionConfig?.requestOptions;
        this.#globalRequestArgOptions = connectionConfig?.requestArgOptions;
        // Initialize resources
        this.users = new Users(this);
        this.userStorageProvider = new UserStorageProvider(this);
        this.groups = new Groups(this);
        this.roles = new Roles(this);
        this.organizations = new Organizations(this);
        this.workflows = new Workflows(this);
        this.clients = new Clients(this);
        this.realms = new Realms(this);
        this.clientScopes = new ClientScopes(this);
        this.clientPolicies = new ClientPolicies(this);
        this.identityProviders = new IdentityProviders(this);
        this.components = new Components(this);
        this.authenticationManagement = new AuthenticationManagement(this);
        this.serverInfo = new ServerInfo(this);
        this.whoAmI = new WhoAmI(this);
        this.attackDetection = new AttackDetection(this);
        this.cache = new Cache(this);
    }
    async auth(credentials) {
        const { accessToken, refreshToken } = await getToken(this.#getTokenSettings(credentials));
        this.#credentials = credentials;
        this.setAccessToken(accessToken);
        this.setRefreshToken(refreshToken);
    }
    #getTokenSettings(credentials) {
        return {
            baseUrl: this.baseUrl,
            realmName: this.realmName,
            scope: this.scope,
            credentials,
            requestOptions: {
                ...this.#requestOptions,
                ...(this.timeout ? { signal: AbortSignal.timeout(this.timeout) } : {}),
            },
        };
    }
    registerTokenProvider(provider) {
        if (this.#tokenProvider) {
            throw new Error("An existing token provider was already registered.");
        }
        this.#tokenProvider = provider;
    }
    setAccessToken(token) {
        this.accessToken = token;
        this.#accessTokenDecoded = decodeToken(token);
    }
    setRefreshToken(token) {
        this.refreshToken = token;
        this.#refreshTokenDecoded = decodeToken(token);
    }
    async getAccessToken() {
        if (this.#tokenProvider) {
            return this.#tokenProvider.getAccessToken();
        }
        if (this.isTokenExpired()) {
            await this.#refreshAccessToken();
        }
        return this.accessToken;
    }
    async #refreshAccessToken() {
        if (!this.refreshToken || !this.#credentials) {
            throw new Error("Cannot refresh token: missing refresh token or credentials");
        }
        if (this.isRefreshTokenExpired()) {
            throw new Error("Cannot refresh token: refresh token has expired");
        }
        const { accessToken, refreshToken } = await getToken(this.#getTokenSettings({
            grantType: "refresh_token",
            clientId: this.#credentials.clientId,
            clientSecret: this.#credentials.clientSecret,
            refreshToken: this.refreshToken,
        }));
        this.setAccessToken(accessToken);
        this.setRefreshToken(refreshToken);
    }
    isTokenExpired() {
        return this.#isExpired(this.#accessTokenDecoded);
    }
    isRefreshTokenExpired() {
        return this.#isExpired(this.#refreshTokenDecoded);
    }
    #isExpired(token) {
        if (typeof token?.exp !== "number") {
            return false;
        }
        const expiresIn = token.exp - Math.ceil(new Date().getTime() / 1000) - MIN_VALIDITY;
        return expiresIn < 0;
    }
    getRequestOptions() {
        return this.#requestOptions;
    }
    getGlobalRequestArgOptions() {
        return this.#globalRequestArgOptions;
    }
    setConfig(connectionConfig) {
        if (typeof connectionConfig.baseUrl === "string" &&
            connectionConfig.baseUrl) {
            this.baseUrl = connectionConfig.baseUrl;
        }
        if (typeof connectionConfig.realmName === "string" &&
            connectionConfig.realmName) {
            this.realmName = connectionConfig.realmName;
        }
        this.#requestOptions = connectionConfig.requestOptions;
    }
}
//# sourceMappingURL=client.js.map